import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Checkbox, CircularProgress, FormControlLabel } from '@mui/material';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import HubDialog from '../common/HubDialog';
import TransactionForm from '../TransactionForm';
import { transactionsApi } from '../../services/api';
import useTransactionFormData from '../../hooks/useTransactionFormData';
import { isTransactionReconciled } from './TransactionReconciliationStatus';

/**
 * Edit an existing transaction inside a modal.
 *
 * @param {object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {string|number} props.transactionId
 * @param {() => void} [props.onSaved]
 */
export default function TransactionFormModal({
    open,
    onClose,
    transactionId,
    onSaved,
}) {
    const { t } = useTranslation();

    const [transaction, setTransaction] = useState(null);
    const [txnLoading, setTxnLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [error, setError] = useState(null);
    const [reconciledAcknowledged, setReconciledAcknowledged] = useState(false);

    const { loading: dataLoading, formProps } = useTransactionFormData({
        active: open,
        transaction,
    });

    useEffect(() => {
        if (!open) {
            setError(null);
            setTransaction(null);
            setReconciledAcknowledged(false);
            return undefined;
        }

        setReconciledAcknowledged(false);

        let cancelled = false;
        (async () => {
            try {
                setTxnLoading(true);
                setError(null);
                const data = await transactionsApi.getById(transactionId);
                if (!cancelled) setTransaction(data);
            } catch (err) {
                console.error('Failed to load transaction:', err);
                if (!cancelled) setError(t('app.editTransaction.errorMessages.loadData'));
            } finally {
                if (!cancelled) setTxnLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [open, transactionId, t]);

    const busy = saving || deleting;
    const loading = dataLoading || txnLoading;
    const isReconciled = transaction ? isTransactionReconciled(transaction) : false;

    const handleSave = async (transactionData) => {
        try {
            setSaving(true);
            setError(null);
            await transactionsApi.update(transactionId, transactionData);
            onSaved?.();
            onClose();
        } catch (err) {
            console.error('Failed to save transaction:', err);
            const apiErrors = err.response?.data?.errors;
            setError(
                (Array.isArray(apiErrors) ? apiErrors.join(' ') : null)
                || err.response?.data?.error
                || t('app.editTransaction.errorMessages.updateTransaction')
            );
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        try {
            setDeleting(true);
            setError(null);
            await transactionsApi.delete(transactionId);
            onSaved?.();
            onClose();
        } catch (err) {
            console.error('Failed to delete transaction:', err);
            const apiError = err.response?.data?.error;
            setError(apiError || t('app.editTransaction.errorMessages.deleteTransaction'));
        } finally {
            setDeleting(false);
        }
    };

    return (
        <HubDialog
            open={open}
            onClose={busy ? undefined : onClose}
            title={t('app.editTransaction.title')}
            icon={ReceiptLongIcon}
            maxWidth="md"
        >
            {loading ? (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                    <CircularProgress />
                </Box>
            ) : !transaction ? (
                <Alert severity="warning">{t('app.editTransaction.notFound')}</Alert>
            ) : (
                <>
                    {isReconciled && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            {t('app.editTransaction.reconciledEditWarning')}
                        </Alert>
                    )}
                    <TransactionForm
                        key={transactionId}
                        {...formProps}
                        initialData={transaction}
                        onSave={handleSave}
                        onDelete={isReconciled ? undefined : handleDelete}
                        onCancel={onClose}
                        saving={saving}
                        deleting={deleting}
                        error={error}
                        submitDisabled={isReconciled && !reconciledAcknowledged}
                        footerStart={isReconciled ? (
                            <FormControlLabel
                                sx={{ m: 0, alignItems: 'flex-start' }}
                                control={(
                                    <Checkbox
                                        checked={reconciledAcknowledged}
                                        onChange={(event) => setReconciledAcknowledged(event.target.checked)}
                                        disabled={saving || deleting}
                                        sx={{ pt: 0.25 }}
                                    />
                                )}
                                label={t('app.editTransaction.reconciledEditAcknowledge')}
                            />
                        ) : null}
                    />
                </>
            )}
        </HubDialog>
    );
}
